import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** Canonical in-content page title — keep in sync with `PageHeader`. */
export const PAGE_TITLE_CLASS = 'text-xl font-semibold tracking-tight lg:text-2xl';

export interface ContentPageHeaderBack {
  label?: string;
  to?: string;
  onClick?: () => void;
}

export interface ContentPageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  badge?: ReactNode;
  back?: ContentPageHeaderBack;
  actions?: ReactNode;
  /** Extra classes for the actions row. Use this to override the default width cap. */
  actionsClassName?: string;
  className?: string;
}

/**
 * In-page title row for AdminShell content (not the primary-colored `PageHeader`).
 * Stacks title and actions on narrow viewports so toolbar buttons do not overflow.
 */
export function ContentPageHeader({
  title,
  description,
  badge,
  back,
  actions,
  actionsClassName,
  className,
}: ContentPageHeaderProps) {
  const backLabel = back?.label ?? 'Back';

  return (
    <header className={cn('mb-6 flex flex-col gap-3', className)}>
      {back ? (
        back.to ? (
          <Button variant="ghost" size="sm" className="-ml-2 w-fit" asChild>
            <Link to={back.to}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {backLabel}
            </Link>
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-2 w-fit"
            onClick={back.onClick}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {backLabel}
          </Button>
        )
      ) : null}

      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className={cn(PAGE_TITLE_CLASS, 'min-w-0 break-words text-foreground')}>
              {title}
            </h1>
            {badge}
          </div>
          {description ? (
            <div className="text-sm text-muted-foreground">{description}</div>
          ) : null}
        </div>

        {actions ? (
          <div
            className={cn(
              'flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:justify-end',
              actionsClassName ?? 'sm:max-w-[min(100%,28rem)]'
            )}
          >
            {actions}
          </div>
        ) : null}
      </div>
    </header>
  );
}

export default ContentPageHeader;
